import React from 'react';
import { Box, Button, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { DocumentWithDownloadUrl as Document } from '@/client';
import { isImage, isImportant, isLink, isNote, isPdf } from '@/utils/documentUtils.ts';
import { isBrowserPreviewableImageByNameOrMime } from '@/utils/upload/uploadUtils.ts';
import CitedPageChips from '@/components/documents/viewers/CitedPageChips.tsx';
import SourceActionsMenu from './SourceActionsMenu.tsx';
import RecordThumbnail from './RecordThumbnail.tsx';
import SemanticSourceCard from './SemanticSourceCard.tsx';

interface RecordPreviewContentProps {
	index: number;
	label: string;
	document: Document | undefined;
	url: string | undefined;
	pages: number[];
	isHighlighted: boolean;
	thumbnailWidth: number;
	thumbnailHeight: number;
	firstCitedPage: number;
	canDownloadDocuments: boolean;
	onOpenDocument: (doc: Document, pages?: number[]) => void;
	onOpenPage: (page: number) => void;
	onPreviewAspectChange: (aspect: number) => void;
}

const RecordPreviewContent: React.FC<RecordPreviewContentProps> = (props: RecordPreviewContentProps) => {
	const {
		index,
		label,
		document,
		url,
		pages,
		isHighlighted,
		thumbnailWidth,
		thumbnailHeight,
		firstCitedPage,
		canDownloadDocuments,
		onOpenDocument,
		onOpenPage,
		onPreviewAspectChange,
	} = props;

	if (!document) {
		return (
			<VStack
				align="stretch"
				gap={1}
				flex="1"
				minW={0}
				py={3}
				borderTopWidth={index === 0 ? 0 : '1px'}
				borderColor={isHighlighted ? 'border.strong' : 'border.default'}
				bg={isHighlighted ? 'brand.muted' : undefined}
			>
				<Text textStyle="label" w="full" minW={0} truncate title={label}>
					{label}
				</Text>
				<Text fontSize="xs" color="fg.muted">
					Record no longer available
				</Text>
			</VStack>
		);
	}

	const isSemanticDocument = isNote(document) || isImportant(document) || isLink(document);

	if (isSemanticDocument) {
		return (
			<SemanticSourceCard
				index={index}
				label={label}
				document={document}
				url={url}
				isHighlighted={isHighlighted}
				canDownloadDocuments={canDownloadDocuments}
				onOpenDocument={onOpenDocument}
			/>
		);
	}

	const canPreviewImage = isImage(document) && isBrowserPreviewableImageByNameOrMime(document.filename);

	if (!isPdf(document) && !canPreviewImage) {
		if (url && canDownloadDocuments) {
			return (
				<Link
					href={url}
					target="_blank"
					rel="noopener noreferrer"
					textDecoration="underline"
					display="block"
					flex="1"
					minW={0}
					truncate
					title={label}
				>
					{label}
				</Link>
			);
		}

		return (
			<Text cursor="default" flex="1" minW={0} truncate title={label}>
				{label}
			</Text>
		);
	}

	const titleRow = (
		<HStack gap={2} align="flex-start" justify="space-between" w="full" minW={0}>
			<Button
				type="button"
				variant="plain"
				h="auto"
				minW={0}
				flex="1"
				overflow="hidden"
				p={0}
				justifyContent="flex-start"
				textAlign="left"
				fontWeight="medium"
				cursor="pointer"
				_hover={{ color: 'brand.fg', textDecoration: 'underline' }}
				onClick={() => onOpenDocument(document, pages)}
			>
				<Text as="span" w="full" truncate title={label}>
					{label}
				</Text>
			</Button>
			<SourceActionsMenu
				onOpen={() => onOpenDocument(document, pages)}
				downloadUrl={canDownloadDocuments ? url : undefined}
			/>
		</HStack>
	);

	const thumbnail = (
		<RecordThumbnail
			document={document}
			url={url}
			width={thumbnailWidth}
			height={thumbnailHeight}
			initialPage={firstCitedPage}
			canDownloadDocuments={canDownloadDocuments}
			onPreviewAspectChange={onPreviewAspectChange}
		/>
	);

	return (
		<Box
			flex="1"
			minW={0}
			py={3}
			borderTopWidth={index === 0 ? 0 : '1px'}
			borderColor={isHighlighted ? 'border.strong' : 'border.default'}
			bg={isHighlighted ? 'brand.muted' : undefined}
		>
			<VStack display={{ base: 'flex', sm: 'none' }} gap={3} align="stretch" w="full" minW={0}>
				{titleRow}
				<HStack gap={3} align="flex-start" w="full" minW={0}>
					{thumbnail}
					<CitedPageChips pages={pages} onOpenPage={onOpenPage} />
				</HStack>
			</VStack>

			<HStack display={{ base: 'none', sm: 'flex' }} gap={3} align="flex-start" w="full" minW={0}>
				{thumbnail}
				<VStack gap={2} align="flex-start" flex="1" minW={0}>
					{titleRow}
					<CitedPageChips pages={pages} onOpenPage={onOpenPage} />
				</VStack>
			</HStack>
		</Box>
	);
};

export default RecordPreviewContent;
